import React, { useState, useEffect } from 'react';
import api from '../utils/api';

/**
 * Modal pentru realocarea unui lead către alt agent.
 * lead: { id, nume?, numeComplet?, isAmbassador?, ambassadorCode? }
 * Afișează checkbox "Transferă și clienții aduși de acest ambasador" doar dacă lead.isAmbassador && lead.ambassadorCode.
 */
export default function ReassignLeadModal({ lead, onClose, onSuccess }) {
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [newAgentId, setNewAgentId] = useState('');
  const [transferReferred, setTransferReferred] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isAmbassador = lead?.isAmbassador === true && lead?.ambassadorCode;

  useEffect(() => {
    const fetchAgents = async () => {
      setLoadingAgents(true);
      try {
        const res = await api.get('/op/subordinates');
        if (res.data.success && res.data.items) {
          setAgents(res.data.items);
          if (res.data.items.length > 0 && !newAgentId) {
            setNewAgentId(String(res.data.items[0].id));
          }
        }
      } catch (err) {
        console.error('Eroare încărcare agenți:', err);
        setError('Nu s-au putut încărca agenții din subordine.');
      } finally {
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newAgentId) {
      setError('Selectează un agent.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await api.put(`/op/leads/${lead.id}/reassign`, {
        newAgentId: parseInt(newAgentId, 10),
        transferReferred: isAmbassador ? transferReferred : false,
      });
      if (res.data.success) {
        onSuccess(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la realocare.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = lead?.numeComplet || lead?.nume || 'Lead';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-md w-full shadow-xl border border-gray-200 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1 text-gray-900 dark:text-white">Realocare Lead</h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          Mută <strong>{displayName}</strong> la un alt agent din echipa ta.
        </p>

        {loadingAgents ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Noul agent</label>
              <select
                value={newAgentId}
                onChange={(e) => setNewAgentId(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#111420] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">— Alege agentul —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.numeComplet || a.email || `#${a.id}`}
                  </option>
                ))}
              </select>
            </div>

            {isAmbassador && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <input
                  type="checkbox"
                  id="transfer-referred"
                  checked={transferReferred}
                  onChange={(e) => setTransferReferred(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="transfer-referred" className="text-sm text-gray-700 dark:text-gray-300">
                  Transferă și clienții aduși de acest ambasador (recomandări cu codul lui)
                </label>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-white/15"
                disabled={submitting}
              >
                Anulează
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                disabled={submitting || !newAgentId}
              >
                {submitting ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Se transferă...
                  </>
                ) : (
                  'Confirmă Transferul'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
